<script lang="ts">
  import StatsLineChart from "@/components/StatsLineChart.svelte";
  import AdminShell from "@/components/AdminShell.svelte";
  import Card from "@/components/Card.svelte";
  import Button from "@/components/Button.svelte";
  import AdminTable from "@/components/AdminTable.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import { api, ApiError, type AdminStats } from "@/api";
  import { routeHref } from "@/router";
  import { token } from "@/stores/session";
  import { formatNumber } from "@/utils/format";
  import { nodeStatusLabels } from "@/config";

  let days = $state<7 | 30 | 90>(30);
  let stats = $state<AdminStats | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let sortBy = $state<"load" | "cpu" | "ram" | "disk">("load");

  function selectDays(d: 7 | 30 | 90) {
    days = d;
  }

  function selectSort(s: "load" | "cpu" | "ram" | "disk") {
    sortBy = s;
  }

  async function load(d: 7 | 30 | 90) {
    if (!$token) {
      return;
    }
    loading = true;
    try {
      stats = await api.adminStats($token, d);
      error = null;
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Не удалось загрузить статистику";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load(days);
  });

  // ponytail: Minimal SVG line chart generator. Native SVG, zero charting libs.
  function buildLinePath(
    data: Array<{ day: string; value: number }>,
    width = 600,
    height = 180,
    pad = 24,
  ): {
    path: string;
    area: string;
    max: number;
    points: Array<{ x: number; y: number; day: string; value: number }>;
  } {
    if (!data.length) {
      return { path: "", area: "", max: 0, points: [] };
    }
    const max = Math.max(...data.map((d) => d.value), 1);
    const step = (width - pad * 2) / Math.max(data.length - 1, 1);
    const points = data.map((d, i) => {
      const x = pad + i * step;
      const y = height - pad - (d.value / max) * (height - pad * 2);
      return { x, y, day: d.day, value: d.value };
    });
    const d = points.reduce(
      (acc, p, i) => `${acc} ${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
      "",
    );
    const area = `${d} L ${points[points.length - 1].x.toFixed(1)} ${height - pad} L ${points[0].x.toFixed(1)} ${height - pad} Z`;
    return { path: d, area, max, points };
  }

  // ponytail: Group entries into day map for timeline alignment
  function alignSeries(
    raw: Array<{ day: string; count?: number; amount?: number }>,
    totalDays: number,
  ): Array<{ day: string; value: number }> {
    const map = new Map(raw.map((r) => [r.day, r.count ?? r.amount ?? 0]));
    const result: Array<{ day: string; value: number }> = [];
    const now = new Date();
    for (let i = totalDays - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 10);
      result.push({ day: key, value: map.get(key) ?? 0 });
    }
    return result;
  }

  const usersSeries = $derived(stats ? alignSeries(stats.userRegistrations, days) : []);
  const serversSeries = $derived(stats ? alignSeries(stats.serversCreated, days) : []);
  const creditsInSeries = $derived(stats ? alignSeries(stats.creditsInflow, days) : []);
  const creditsOutSeries = $derived(stats ? alignSeries(stats.creditsOutflow, days) : []);

  const userChart = $derived(buildLinePath(usersSeries));
  const serverChart = $derived(buildLinePath(serversSeries));
  const creditInChart = $derived(buildLinePath(creditsInSeries));
  const creditOutChart = $derived(buildLinePath(creditsOutSeries));

  const totalNewUsers = $derived(usersSeries.reduce((s, d) => s + d.value, 0));
  const totalNewServers = $derived(serversSeries.reduce((s, d) => s + d.value, 0));
  const totalCreditsIn = $derived(creditsInSeries.reduce((s, d) => s + d.value, 0));
  const totalCreditsOut = $derived(creditsOutSeries.reduce((s, d) => s + d.value, 0));

  const sortedLoadedServers = $derived.by(() => {
    const list = stats?.loadedServers ? [...stats.loadedServers] : [];
    switch (sortBy) {
      case "cpu":
        return list.sort((a, b) => b.cpuPercent - a.cpuPercent);
      case "ram":
        return list.sort((a, b) => b.memoryUsedMb - a.memoryUsedMb);
      case "disk":
        return list.sort((a, b) => b.diskUsedMb - a.diskUsedMb);
      case "load":
      default:
        return list.sort(
          (a, b) =>
            Math.max(b.cpuUsagePercent, b.memUsagePercent) -
            Math.max(a.cpuUsagePercent, a.memUsagePercent),
        );
    }
  });
</script>

<AdminShell section="stats">
  <div class="stats-header">
    <div class="period-toggle" role="group" aria-label="Период">
      <Button
        variant={days === 7 ? "primary" : "ghost"}
        size="compact"
        onclick={() => selectDays(7)}>7 дней</Button
      >
      <Button
        variant={days === 30 ? "primary" : "ghost"}
        size="compact"
        onclick={() => selectDays(30)}>30 дней</Button
      >
      <Button
        variant={days === 90 ? "primary" : "ghost"}
        size="compact"
        onclick={() => selectDays(90)}>90 дней</Button
      >
    </div>
  </div>

  {#if error}
    <p class="form-error">{error}</p>
  {/if}

  {#if loading && !stats}
    <LoadingState message="Считаем статистику…" />
  {:else if stats}
    <section class="admin-metrics">
      <div class="metric-card">
        <span>Новые пользователи</span>
        <strong>+{totalNewUsers}</strong>
        <small>за последние {days} дн.</small>
      </div>
      <div class="metric-card">
        <span>Создано серверов</span>
        <strong>+{totalNewServers}</strong>
        <small>за последние {days} дн.</small>
      </div>
      <div class="metric-card">
        <span>Начислено кредитов</span>
        <strong>+{formatNumber(totalCreditsIn)}</strong>
        <small>бонусы, реклама, ручные</small>
      </div>
      <div class="metric-card">
        <span>Списано кредитов</span>
        <strong>-{formatNumber(totalCreditsOut)}</strong>
        <small>аренда, слоты, бэкапы</small>
      </div>
    </section>

    <div class="stats-charts-grid">
      <Card eyebrow="Динамика" title="Регистрации пользователей">
        <div class="chart-box">
          <StatsLineChart
            label="Регистрации пользователей"
            series={[{ label: "Регистрации", color: "var(--mars-light)", ...userChart }]}
          />
          <div class="chart-meta">
            <small>{usersSeries[0]?.day}</small>
            <small>Пик: {userChart.max}</small>
            <small>{usersSeries[usersSeries.length - 1]?.day}</small>
          </div>
        </div>
      </Card>

      <Card eyebrow="Динамика" title="Создание серверов">
        <div class="chart-box">
          <StatsLineChart
            label="Создание серверов"
            series={[{ label: "Серверы", color: "#3b82f6", ...serverChart }]}
          />
          <div class="chart-meta">
            <small>{serversSeries[0]?.day}</small>
            <small>Пик: {serverChart.max}</small>
            <small>{serversSeries[serversSeries.length - 1]?.day}</small>
          </div>
        </div>
      </Card>

      <Card eyebrow="Экономика" title="Движение кредитов">
        <div class="chart-box">
          <StatsLineChart
            label="Движение кредитов"
            series={[
              { label: "Начисления", color: "#10b981", ...creditInChart },
              { label: "Списания", color: "#f43f5e", ...creditOutChart, dashed: true },
            ]}
          />
          <div class="chart-legend">
            <span class="legend-item"
              ><span class="dot in"></span> Начисления (+{totalCreditsIn})</span
            >
            <span class="legend-item"
              ><span class="dot out"></span> Списания (-{totalCreditsOut})</span
            >
          </div>
        </div>
      </Card>

      <Card eyebrow="Инфраструктура" title="Нагрузка нод">
        <div class="node-bars">
          {#if stats.nodesUsage.length === 0}
            <p class="muted">Нод нет.</p>
          {:else}
            {#each stats.nodesUsage as n (n.id)}
              <div class="node-bar-row">
                <div class="node-bar-label">
                  <span
                    ><strong>{n.name}</strong>
                    <small class="status-sub">({nodeStatusLabels[n.status] ?? n.status})</small
                    ></span
                  >
                  <span
                    >{n.serverCount} / {n.capacity} серверов ({Math.round(
                      (n.serverCount / Math.max(n.capacity, 1)) * 100,
                    )}%)</span
                  >
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill"
                    style:width={`${Math.min(100, Math.round((n.serverCount / Math.max(n.capacity, 1)) * 100))}%`}
                  ></div>
                </div>
                <div class="node-bar-sub">
                  <small
                    >ОЗУ: {n.allocatedMemMb} МБ / {n.memTotalMb
                      ? `${n.memTotalMb} МБ`
                      : "не ограничена"}</small
                  >
                </div>
              </div>
            {/each}
          {/if}
        </div>
      </Card>
    </div>

    <div class="stats-servers-section">
      <Card eyebrow="Мониторинг" title="Самые нагруженные серверы">
        <div class="loaded-toolbar">
          <div class="loaded-sort-group" role="group" aria-label="Сортировка">
            <Button
              variant={sortBy === "load" ? "primary" : "ghost"}
              size="compact"
              onclick={() => selectSort("load")}>По % нагрузки</Button
            >
            <Button
              variant={sortBy === "cpu" ? "primary" : "ghost"}
              size="compact"
              onclick={() => selectSort("cpu")}>По процессору</Button
            >
            <Button
              variant={sortBy === "ram" ? "primary" : "ghost"}
              size="compact"
              onclick={() => selectSort("ram")}>По ОЗУ</Button
            >
            <Button
              variant={sortBy === "disk" ? "primary" : "ghost"}
              size="compact"
              onclick={() => selectSort("disk")}>По диску</Button
            >
          </div>
          <small class="loaded-count"
            >Активных серверов с телеметрией: {stats.loadedServers?.length ?? 0}</small
          >
        </div>

        {#if !stats.loadedServers || stats.loadedServers.length === 0}
          <p class="muted">Нет активных серверов с телеметрией.</p>
        {:else}
          <AdminTable
            label="Таблица самых нагруженных серверов. Прокрутите горизонтально для просмотра всех столбцов"
          >
            <thead>
              <tr>
                <th>Сервер</th>
                <th>Владелец</th>
                <th>Нода</th>
                <th>Процессор</th>
                <th>ОЗУ</th>
                <th>Диск</th>
                <th>Статус нагрузки</th>
              </tr>
            </thead>
            <tbody>
              {#each sortedLoadedServers as s (s.id)}
                <tr>
                  <td>
                    <a class="table-link" href={routeHref("admin-server", { id: s.id })}>
                      <strong>{s.name}</strong>
                    </a>
                  </td>
                  <td>
                    <a class="table-link" href={routeHref("admin-user", { id: s.ownerId })}>
                      {s.ownerUsername}
                    </a>
                  </td>
                  <td>
                    {#if s.nodeId}
                      <a class="table-link" href={routeHref("admin-node", { id: s.nodeId })}
                        >{s.nodeName}</a
                      >
                    {:else}
                      {s.nodeName}
                    {/if}
                  </td>
                  <td>
                    <div class="metric-progress-cell">
                      <div class="metric-val-row">
                        <span>{s.cpuPercent}%</span>
                        <small class="dim">/ {s.cpuLimitPercent}%</small>
                      </div>
                      <div class="progress-bar mini">
                        <div
                          class="progress-fill"
                          class:danger={s.cpuUsagePercent >= 90}
                          class:warn={s.cpuUsagePercent >= 75 && s.cpuUsagePercent < 90}
                          style:width={`${Math.min(100, s.cpuUsagePercent)}%`}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="metric-progress-cell">
                      <div class="metric-val-row">
                        <span>{s.memoryUsedMb} МБ</span>
                        <small class="dim">/ {s.memoryLimitMb} МБ ({s.memUsagePercent}%)</small>
                      </div>
                      <div class="progress-bar mini">
                        <div
                          class="progress-fill"
                          class:danger={s.memUsagePercent >= 90}
                          class:warn={s.memUsagePercent >= 75 && s.memUsagePercent < 90}
                          style:width={`${Math.min(100, s.memUsagePercent)}%`}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="dim">{s.diskUsedMb} / {s.diskLimitMb} МБ</span>
                  </td>
                  <td>
                    {#if s.cpuUsagePercent >= 90 || s.memUsagePercent >= 90}
                      <span class="load-pill load-critical">Перегрузка</span>
                    {:else if s.cpuUsagePercent >= 75 || s.memUsagePercent >= 75}
                      <span class="load-pill load-warning">Повышенная</span>
                    {:else}
                      <span class="load-pill load-ok">В норме</span>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </AdminTable>
        {/if}
      </Card>
    </div>

    <div class="stats-breakdown-grid">
      <Card eyebrow="Распределение" title="Серверы по ядрам">
        <div class="pill-list">
          {#each stats.serversByCore as c (c.core)}
            <div class="pill-item">
              <span class="pill-name">{c.core}</span>
              <strong class="pill-val">{c.count}</strong>
            </div>
          {/each}
        </div>
      </Card>

      <Card eyebrow="Распределение" title="Серверы по играм">
        <div class="pill-list">
          {#each stats.serversByGame as g (g.game)}
            <div class="pill-item">
              <span class="pill-name">{g.game}</span>
              <strong class="pill-val">{g.count}</strong>
            </div>
          {/each}
        </div>
      </Card>
    </div>
  {/if}
</AdminShell>

<style>
  .stats-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
  }
  .period-toggle {
    display: flex;
    gap: 0.4rem;
  }
  .stats-charts-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }
  .stats-breakdown-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
  .chart-box {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.5rem;
  }
  .chart-meta {
    display: flex;
    justify-content: space-between;
    color: var(--dim);
    font-size: 0.7rem;
  }
  .chart-legend {
    display: flex;
    gap: 1rem;
    font-size: 0.72rem;
    margin-top: 0.2rem;
  }
  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--dim);
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .dot.in {
    background: #10b981;
  }
  .dot.out {
    background: #f43f5e;
  }
  .node-bars {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    padding: 1.5rem;
  }
  .node-bar-row {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .node-bar-label {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    font-size: 0.75rem;
  }
  .status-sub {
    color: var(--dim);
  }
  .progress-bar {
    height: 8px;
    background: rgb(255 255 255 / 0.05);
    border: 1px solid var(--line);
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: var(--mars-light);
    transition: width 0.3s ease;
  }
  .node-bar-sub {
    font-size: 0.68rem;
    color: var(--dim);
  }
  .pill-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .pill-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.75rem;
    border: 1px solid var(--line);
    background: var(--surface);
    font-size: 0.75rem;
  }
  .pill-name {
    color: var(--dim);
    text-transform: uppercase;
  }
  .pill-val {
    color: var(--white);
    font-family: var(--font-display);
  }

  @media (max-width: 840px) {
    .stats-charts-grid,
    .stats-breakdown-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 520px) {
    .chart-box,
    .node-bars {
      padding-inline: 1rem;
    }
  }

  .stats-servers-section {
    margin-bottom: 1rem;
  }
  .loaded-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.8rem;
    flex-wrap: wrap;
  }
  .loaded-sort-group {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
  }
  .loaded-count {
    color: var(--dim);
    font-size: 0.75rem;
  }
  .metric-progress-cell {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 110px;
  }
  .metric-val-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 0.75rem;
  }
  .progress-bar.mini {
    height: 4px;
  }
  .progress-fill.warn {
    background: #eab308;
  }
  .progress-fill.danger {
    background: #ef4444;
  }
  .load-pill {
    display: inline-block;
    padding: 0.15rem 0.45rem;
    border-radius: 9999px;
    font-size: 0.68rem;
    font-weight: 500;
  }
  .load-critical {
    background: rgb(239 68 68 / 0.18);
    color: #ef4444;
    border: 1px solid rgb(239 68 68 / 0.35);
  }
  .load-warning {
    background: rgb(234 179 8 / 0.18);
    color: #eab308;
    border: 1px solid rgb(234 179 8 / 0.35);
  }
  .load-ok {
    background: rgb(16 185 129 / 0.18);
    color: #10b981;
    border: 1px solid rgb(16 185 129 / 0.35);
  }
</style>
